{/* DRAFT — owner must have legal review before launch */}
import { ArrowLeft } from 'lucide-react';

interface TermsPageProps {
  onNavigate: (page: string) => void;
}

const LAST_UPDATED = 'June 2026';

/**
 * Terms of Service — DRAFT. Built from a standard SaaS template plus the real
 * facts about how Karyva operates (Stripe billing, 14-day trial, data stored in
 * Postgres on the company's own infrastructure). Bracketed placeholders
 * ([Legal name], [Jurisdiction], …) must be completed and the whole document
 * reviewed by a lawyer before this goes live.
 */
export function TermsPage({ onNavigate }: TermsPageProps) {
  return (
    <div className="bg-white">
      <section className="pt-16 pb-10 px-6 bg-gradient-to-b from-[#f8f9fa] to-white">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => onNavigate('home')}
            className="inline-flex items-center gap-1.5 text-sm text-[#556B7C] hover:text-[#2A3B4C] transition-colors mb-6"
          >
            <ArrowLeft size={15} /> Back to home
          </button>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#2A3B4C] mb-2">Terms of Service</h1>
          <p className="text-[#556B7C] text-sm">Last updated: {LAST_UPDATED}</p>
          <div className="mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            Draft for review. These terms are pending legal review and may change before launch.
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto space-y-8 text-[#3a4a59] text-sm leading-relaxed">

          <Clause n="1" title="Agreement to terms">
            These Terms of Service ("Terms") are a binding agreement between you and
            [Company legal name] ("Karyva", "we", "us") governing your access to and use
            of the Karyva project &amp; portfolio management platform at app.karyva.com
            and related websites and services (the "Service"). By creating an account or
            using the Service, you agree to these Terms. If you are using the Service on
            behalf of an organisation, you represent that you are authorised to bind that
            organisation.
          </Clause>

          <Clause n="2" title="The Service">
            Karyva is a multi-tenant, cloud-hosted Software-as-a-Service application for
            managing projects, portfolios, programs, tasks, and related work. Features
            available to you depend on your subscription plan. We may update, add, or
            remove features over time.
          </Clause>

          <Clause n="3" title="Accounts &amp; eligibility">
            You must provide accurate account information and keep it current. You are
            responsible for safeguarding your credentials and for all activity under your
            account. Notify us promptly of any unauthorised use. You must be at least 18
            years old (or the age of majority in your jurisdiction) to use the Service.
          </Clause>

          <Clause n="4" title="Trials, subscriptions &amp; billing">
            New organisations receive a 14-day free trial with access to the core
            platform; no payment details are required to start. Paid plans (Starter,
            Growth, and Business) are billed monthly in advance through our payment
            processor, Stripe. Subscriptions renew automatically until cancelled. You may
            cancel at any time from your billing settings; cancellation takes effect at
            the end of the current billing period. Fees are non-refundable except where
            required by law. When a trial ends without upgrade, your workspace becomes
            read-only — your data is retained, not deleted.
          </Clause>

          <Clause n="5" title="Acceptable use">
            You agree not to: (a) use the Service to violate any law or third-party right;
            (b) upload malicious code or attempt to disrupt or gain unauthorised access to
            the Service or other tenants' data; (c) reverse engineer or resell the Service
            except as permitted by law; or (d) use the Service to store or transmit
            content you do not have the right to. We may suspend accounts that breach this
            section.
          </Clause>

          <Clause n="6" title="Your data &amp; ownership">
            You retain all rights to the content and data you submit to the Service ("Customer
            Data"). You grant us a limited licence to host, process, and display Customer
            Data solely to provide and improve the Service. Customer Data is stored in a
            PostgreSQL database on infrastructure we operate and control. You can export or
            delete your data at any time from within the product (see our{' '}
            <button onClick={() => onNavigate('privacy')} className="text-[#DAA520] hover:underline">Privacy Policy</button>).
          </Clause>

          <Clause n="7" title="Subprocessors">
            We use a small number of third-party subprocessors to operate the Service:
            Stripe (payment processing), Resend (transactional email delivery), and an AI
            service provider (to power optional in-product AI assistant features). These
            providers process data only as needed to perform their function. A current list
            is maintained in our Privacy Policy.
          </Clause>

          <Clause n="8" title="Service availability">
            We work to keep the Service available and reliable but do not guarantee
            uninterrupted access. The Service is provided "as is" and "as available". We
            may perform maintenance, and we may modify or discontinue features with
            reasonable notice.
          </Clause>

          <Clause n="9" title="Disclaimers &amp; limitation of liability">
            To the maximum extent permitted by law, Karyva disclaims all implied
            warranties, and our aggregate liability arising out of or relating to the
            Service will not exceed the fees you paid to us in the 12 months preceding the
            claim. We are not liable for indirect, incidental, or consequential damages.
          </Clause>

          <Clause n="10" title="Termination">
            You may stop using the Service and close your account at any time. We may
            suspend or terminate your access for breach of these Terms or non-payment.
            Upon termination, you may export your Customer Data for a reasonable period,
            after which it may be deleted in accordance with our retention practices.
          </Clause>

          <Clause n="11" title="Changes to these Terms">
            We may update these Terms from time to time. If we make material changes, we
            will notify you (for example, by email or an in-product notice). Continued use
            of the Service after changes take effect constitutes acceptance.
          </Clause>

          <Clause n="12" title="Governing law">
            These Terms are governed by the laws of [Jurisdiction], without regard to its
            conflict-of-laws rules. Disputes will be resolved in the courts of
            [Jurisdiction].
          </Clause>

          <Clause n="13" title="Contact">
            Questions about these Terms? Email{' '}
            <a href="mailto:legal@karyva.com" className="text-[#DAA520] hover:underline">legal@karyva.com</a>.
          </Clause>

        </div>
      </section>
    </div>
  );
}

function Clause({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-base font-bold text-[#2A3B4C] mb-2">{n}. {title}</h2>
      <p>{children}</p>
    </div>
  );
}
